package com.evoapps.evoshift.ui.util

import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.BeachAccess
import androidx.compose.material.icons.filled.DownhillSkiing
import androidx.compose.material.icons.filled.Event
import androidx.compose.material.icons.filled.Weekend
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.domain.model.SpecialDay
import com.evoapps.evoshift.domain.model.SpecialDayKind
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.special_day_bridge
import evoshift.composeapp.generated.resources.special_day_detail_bridge
import evoshift.composeapp.generated.resources.special_day_detail_event
import evoshift.composeapp.generated.resources.special_day_detail_long_weekend
import evoshift.composeapp.generated.resources.special_day_detail_long_weekend_holiday
import evoshift.composeapp.generated.resources.special_day_detail_summer
import evoshift.composeapp.generated.resources.special_day_detail_winter
import evoshift.composeapp.generated.resources.special_day_long_weekend
import evoshift.composeapp.generated.resources.special_day_more
import evoshift.composeapp.generated.resources.special_day_summer
import evoshift.composeapp.generated.resources.special_day_winter_break
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

// SH-227: a day's local events and special days, kept lean on purpose: one small marker (an icon
// and a short label cut to fit, "+1" when there is more) and one line with everything on tap or
// hover, read out whole by screen readers. Like the weather (SH-226) it takes the colour of what
// it sits on (LocalContentColor); each kind has its own icon, so nothing is told by colour alone.

/** Each day's special days on the daily results, by ISO date (empty: none). */
val LocalDaySpecials = staticCompositionLocalOf<Map<String, DaySpecials>> { emptyMap() }

/** A day's marker, by ISO date. */
fun specialDayMarkerTag(isoDate: String) = "special_day_$isoDate"

fun SpecialDay.icon(): ImageVector = when (kind) {
    SpecialDayKind.LOCAL_EVENT -> Icons.Default.Event
    SpecialDayKind.LONG_WEEKEND -> Icons.Default.Weekend
    SpecialDayKind.WINTER_BREAK -> Icons.Default.DownhillSkiing
    SpecialDayKind.SUMMER_HOLIDAYS -> Icons.Default.BeachAccess
}

/** The marker's short label: the event's name, "Long weekend", "Bridge day", "Winter break", "School holidays". */
@Composable
fun specialDayLabel(day: SpecialDay): String = when (day.kind) {
    SpecialDayKind.LOCAL_EVENT -> day.name.orEmpty()
    SpecialDayKind.LONG_WEEKEND -> stringResource(if (day.bridge) Res.string.special_day_bridge else Res.string.special_day_long_weekend)
    SpecialDayKind.WINTER_BREAK -> stringResource(Res.string.special_day_winter_break)
    SpecialDayKind.SUMMER_HOLIDAYS -> stringResource(Res.string.special_day_summer)
}

/** "4 July", "1–4 July", "30 June – 2 July". */
@Composable
fun dayRangeText(from: LocalDate, to: LocalDate): String = when {
    from == to -> dayAndMonth(from)
    from.year == to.year && from.month == to.month -> "${from.day}–${dayAndMonth(to)}"
    else -> "${dayAndMonth(from)} – ${dayAndMonth(to)}"
}

/** One reason in words, with its whole period: "Open'er, 1–4 July", "Winter break, pomorskie voivodeship: 19 January – 1 February". */
@Composable
fun specialDayDetail(day: SpecialDay): String {
    val period = dayRangeText(day.from, day.to)
    return when (day.kind) {
        SpecialDayKind.LOCAL_EVENT -> stringResource(Res.string.special_day_detail_event, day.name.orEmpty(), period)
        SpecialDayKind.LONG_WEEKEND -> when {
            day.bridge -> stringResource(Res.string.special_day_detail_bridge, period)
            day.holidayName != null -> stringResource(Res.string.special_day_detail_long_weekend_holiday, period, day.holidayName!!)
            else -> stringResource(Res.string.special_day_detail_long_weekend, period)
        }
        SpecialDayKind.WINTER_BREAK -> stringResource(Res.string.special_day_detail_winter, day.voivodeship?.polishName.orEmpty(), period)
        SpecialDayKind.SUMMER_HOLIDAYS -> stringResource(Res.string.special_day_detail_summer, period)
    }
}

/** The whole day in one line: every reason, the marker's first, joined by " · ". */
@Composable
fun specialDaysLine(day: DaySpecials): String = day.items.map { specialDayDetail(it) }.joinToString(" · ")

/**
 * A day's marker: the first reason's icon and short label (cut to fit [maxWidth]) and "+N" for the
 * rest. It always sits on something tapped to open the day (a roster header or row, a daily
 * result), so it takes no tap itself: the day it opens shows the whole line ([SpecialDaysLine]),
 * a mouse hovering over it shows the line as a tooltip, and screen readers read the line out.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SpecialDayMarker(
    day: DaySpecials,
    modifier: Modifier = Modifier,
    iconSize: Dp = 12.dp,
    maxWidth: Dp = Dp.Unspecified,
    textStyle: TextStyle = MaterialTheme.typography.labelSmall
) {
    val line = specialDaysLine(day)
    val tooltip = rememberTooltipState(isPersistent = false)
    val scope = rememberCoroutineScope()
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = { PlainTooltip { Text(line) } },
        state = tooltip,
        modifier = modifier,
        // No long-press of its own: holding a day adds an event.
        enableUserInput = false
    ) {
        Row(
            modifier = Modifier
                .then(if (maxWidth != Dp.Unspecified) Modifier.widthIn(max = maxWidth) else Modifier)
                .testTag(specialDayMarkerTag(day.date.toString()))
                // A mouse over it shows the line; nothing is consumed, so a click still opens the day.
                .pointerInput(line) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            if (event.changes.none { it.type == PointerType.Mouse }) continue
                            when (event.type) {
                                PointerEventType.Enter -> scope.launch { tooltip.show() }
                                PointerEventType.Exit -> tooltip.dismiss()
                                else -> Unit
                            }
                        }
                    }
                }
                // One node read out as the whole line; the texts stay in the tree (contrast checks).
                .semantics(mergeDescendants = true) { contentDescription = line },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(3.dp)
        ) {
            Icon(day.primary.icon(), contentDescription = null, tint = LocalContentColor.current, modifier = Modifier.size(iconSize))
            Text(
                specialDayLabel(day.primary),
                style = textStyle,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f, fill = false)
            )
            if (day.more > 0) Text(stringResource(Res.string.special_day_more, day.more), style = textStyle, maxLines = 1)
        }
    }
}

/** A day's whole line, by ISO date (the day sheet and pane, a daily result's card). */
fun specialDayLineTag(isoDate: String) = "special_day_line_$isoDate"

/**
 * The day's whole line where there is room for it (the roster's day sheet and pane, a daily
 * result's card): the first reason's icon and everything in words, at most two lines.
 */
@Composable
fun SpecialDaysLine(
    day: DaySpecials,
    modifier: Modifier = Modifier,
    iconSize: Dp = 14.dp,
    textStyle: TextStyle = MaterialTheme.typography.labelMedium
) {
    val line = specialDaysLine(day)
    Row(
        modifier = modifier
            .testTag(specialDayLineTag(day.date.toString()))
            .semantics(mergeDescendants = true) { contentDescription = line },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Icon(day.primary.icon(), contentDescription = null, tint = LocalContentColor.current, modifier = Modifier.size(iconSize))
        Text(line, style = textStyle, maxLines = 2, overflow = TextOverflow.Ellipsis)
    }
}

/**
 * The hook for SH-225's comparisons ("+40%, Open'er"), as weatherComparisonNote is for the
 * weather: the day's first reason in a word or two (an event's name, "long weekend", "winter
 * break"), or null on an ordinary day. A comparison reads `LocalDaySpecials` (or its view model's
 * special days) for each date and appends this to the change.
 */
@Composable
fun specialDayComparisonNote(day: DaySpecials?): String? {
    val primary = day?.primary ?: return null
    val label = specialDayLabel(primary)
    return if (primary.kind == SpecialDayKind.LOCAL_EVENT) label else label.lowercase()
}
